import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle2, ShieldCheck, Loader2, MapPin, Landmark, HeartHandshake, XCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader } from '../components/Shared';
import { usePitaLogo } from '../components/PitaCTA';

const REQUEST_TYPES = [
  {
    value: 'ward_access',
    label: 'Ward access',
    icon: MapPin,
    blurb: 'For Councillors, Councillor candidates, ward campaigns, and community groups who want PITA for their ward.',
  },
  {
    value: 'citywide_access',
    label: 'Mayor / Citywide access',
    icon: Landmark,
    blurb: 'For Mayors, Mayor candidates, and citywide campaigns who want PITA across Kawartha Lakes.',
  },
  {
    value: 'supporter_only',
    label: 'Supporter only',
    icon: HeartHandshake,
    blurb: 'Support and share PITA publicly. Does not include report access or private issue information.',
  },
];

const WARD_ROLES = ['Councillor', 'Councillor candidate', 'Ward campaign', 'Community group', 'Other'];
const CITYWIDE_ROLES = ['Mayor', 'Mayor candidate', 'Citywide campaign', 'Community group', 'Other'];
const WARDS = ['1', '2', '3', '4', '5', '6', '7', '8'];

const SUPPORTER_CAN = [
  'Publicly support PITA if approved',
  'Share PITA with their community',
  'Request to be listed as a supporter',
  'Receive public-facing embed and share materials if approved',
];
const SUPPORTER_CANNOT = [
  'See private reports',
  'See private resident information',
  'Receive copies of issue reports',
  'Follow up on private reports',
  'Access ward or member dashboards',
];

const EMPTY = {
  name: '', email: '', phone: '', organization: '',
  request_type: '', ward: '', role: '', website_url: '',
  wants_embed: false, wants_followup: false, wants_citywide_visibility: false,
  message: '', consent: false, website: '',
};

export default function PitaRequestAccess() {
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pitaLogo = usePitaLogo('support');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickType = (v) => {
    setForm((f) => ({
      ...f, request_type: v, ward: '', role: '',
      wants_followup: false, wants_citywide_visibility: false,
    }));
  };

  const emailOk = /.+@.+\..+/.test(form.email);
  const t = form.request_type;

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Please enter your name'); return; }
    if (!emailOk) { toast.error('Please enter a valid email address'); return; }
    if (!t) { toast.error('Please choose a request type'); return; }
    if (t === 'ward_access' && !form.ward) { toast.error('Please choose your ward number'); return; }
    if (!form.consent) { toast.error('Please confirm the consent checkbox'); return; }
    setBusy(true);
    try {
      await api.post('/pita/request-access', form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="pita-request-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
        <h1 className="font-serif font-black text-[#1a2744] text-2xl sm:text-3xl mt-4">Request received</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Thanks for your interest in PITA. Every request is reviewed personally. No account is created, no report
          access is granted, and nothing goes live without approval from both sides. The campaign may contact you
          about this request.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="h-12 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-center" data-testid="pita-request-success-tracker-btn">
            <Link to="/issue-tracker">See the Ward 8 Tracker</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="pita-request-success-support-btn">
            <Link to="/support-pita">Support PITA</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pita-request-page">
      <PageHeader
        kicker="PUBLIC ISSUE TRACKER APP (PITA)"
        title="Request PITA Access"
        subtitle="Request an embed for your website, ward or citywide access, or supporter-only materials. Every request is reviewed personally, and nothing is activated automatically."
        logo={pitaLogo?.url}
        logoAlt={pitaLogo?.alt}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="rounded-2xl border border-[#C9A84C]/40 bg-[#C9A84C]/10 p-4 flex gap-3 mb-6" data-testid="pita-request-approval-note">
          <ShieldCheck className="h-5 w-5 text-[#7A5C12] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Approval-based only.</strong> Sending this request does not create an account, publish anything,
            or activate any access. Member accounts are only created manually after approval. Report access is scoped
            by ward or citywide, and private resident data is never shared without the right permissions.
          </p>
        </div>

        <Card>
          <CardContent className="p-5 sm:p-7 space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-2">Request type *</label>
              <RadioGroup value={t} onValueChange={pickType} className="space-y-2.5" data-testid="pita-request-type-group">
                {REQUEST_TYPES.map((rt) => {
                  const Icon = rt.icon;
                  const active = t === rt.value;
                  return (
                    <label
                      key={rt.value}
                      className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${active ? 'border-[#1a2744] bg-[#1a2744]/5' : 'border-slate-200 hover:border-slate-300'}`}
                      data-testid={`pita-request-type-${rt.value}`}
                    >
                      <RadioGroupItem value={rt.value} className="mt-0.5" />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 font-bold text-[#1a2744] text-sm">
                          <Icon className="h-4 w-4 text-[#C9A84C]" /> {rt.label}
                        </span>
                        <span className="block text-xs text-slate-500 mt-1 leading-relaxed">{rt.blurb}</span>
                      </span>
                    </label>
                  );
                })}
              </RadioGroup>
            </div>

            {t === 'supporter_only' && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="pita-supporter-only-notice">
                <p className="text-sm font-bold text-[#1a2744]">
                  Supporter-only access does not include report access or private issue information.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1.5">Supporters can</p>
                    <ul className="space-y-1.5">
                      {SUPPORTER_CAN.map((x) => (
                        <li key={x} className="flex gap-2 text-xs text-slate-600 leading-snug">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" /> {x}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1.5">Supporters cannot</p>
                    <ul className="space-y-1.5">
                      {SUPPORTER_CANNOT.map((x) => (
                        <li key={x} className="flex gap-2 text-xs text-slate-600 leading-snug">
                          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" /> {x}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  This matters because report details may contain private resident information. Supporter-only access
                  never includes private report access.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Your name *</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="h-11" data-testid="pita-request-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="h-11" data-testid="pita-request-email-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Phone (optional)</label>
                <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="h-11" data-testid="pita-request-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Organization or campaign (optional)</label>
                <Input value={form.organization} onChange={(e) => set('organization', e.target.value)} className="h-11" data-testid="pita-request-org-input" />
              </div>

              {t === 'ward_access' && (
                <>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Ward number *</label>
                    <Select value={form.ward} onValueChange={(v) => set('ward', v)}>
                      <SelectTrigger className="h-11" data-testid="pita-request-ward-select"><SelectValue placeholder="Choose your ward" /></SelectTrigger>
                      <SelectContent>{WARDS.map((w) => <SelectItem key={w} value={w}>Ward {w}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1">Your role</label>
                    <Select value={form.role} onValueChange={(v) => set('role', v)}>
                      <SelectTrigger className="h-11" data-testid="pita-request-role-select"><SelectValue placeholder="Choose your role" /></SelectTrigger>
                      <SelectContent>{WARD_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {t === 'citywide_access' && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-600 mb-1">Citywide or Mayor role</label>
                  <Select value={form.role} onValueChange={(v) => set('role', v)}>
                    <SelectTrigger className="h-11" data-testid="pita-request-role-select"><SelectValue placeholder="Choose your role" /></SelectTrigger>
                    <SelectContent>{CITYWIDE_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {(t === 'ward_access' || t === 'citywide_access') && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-600 mb-1">Website where you want to embed PITA</label>
                  <Input value={form.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="https://" className="h-11" data-testid="pita-request-website-input" />
                </div>
              )}
            </div>

            {(t === 'ward_access' || t === 'citywide_access') && (
              <div className="space-y-2.5 rounded-xl border border-slate-200 p-3.5" data-testid="pita-request-access-options">
                <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Access options (approval-only)</p>
                <label className="flex items-center gap-2.5 text-sm text-slate-700 font-medium cursor-pointer">
                  <Checkbox checked={form.wants_embed} onCheckedChange={(v) => set('wants_embed', !!v)} data-testid="pita-request-embed-checkbox" />
                  I want to embed PITA on my website
                </label>
                {t === 'citywide_access' && (
                  <label className="flex items-center gap-2.5 text-sm text-slate-700 font-medium cursor-pointer">
                    <Checkbox checked={form.wants_citywide_visibility} onCheckedChange={(v) => set('wants_citywide_visibility', !!v)} data-testid="pita-request-citywide-checkbox" />
                    I am requesting citywide report visibility
                  </label>
                )}
                <label className="flex items-center gap-2.5 text-sm text-slate-700 font-medium cursor-pointer">
                  <Checkbox checked={form.wants_followup} onCheckedChange={(v) => set('wants_followup', !!v)} data-testid="pita-request-followup-checkbox" />
                  I am requesting report follow-up access
                </label>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Report access is never automatic. It is scoped by ward or citywide and granted only after personal
                  review and approval.
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Message or question (optional)</label>
              <Textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4} placeholder="Tell us what you're looking for…" data-testid="pita-request-message-input" />
            </div>

            {/* Honeypot — hidden from real users */}
            <input type="text" value={form.website} onChange={(e) => set('website', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer border-t pt-4" data-testid="pita-request-consent-label">
              <Checkbox checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-0.5" data-testid="pita-request-consent-checkbox" />
              <span>
                I understand this is a request only. No account will be created, no report access will be granted, no
                embed will be activated, and nothing will be published unless the campaign approves it. The campaign
                may contact me about this request.
              </span>
            </label>

            <Button onClick={submit} disabled={busy} className="w-full h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="pita-request-submit-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-1.5" /> Send Request</>}
            </Button>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 mt-6 text-center" data-testid="pita-request-disclaimer">
          This is not an official City of Kawartha Lakes service portal.
        </p>
      </div>
    </div>
  );
}
